import React from 'react'
import { X } from 'lucide-react'
import { getRoomDisplayName } from '../helpers'
import type { useTurbineDetailViewModel } from '../useTurbineDetailViewModel'

type VM = ReturnType<typeof useTurbineDetailViewModel>

interface PhotoModalProps {
  canDeletePhotos: VM['canDeletePhotos']
  modalPhoto: VM['modalPhoto']
  setModalPhoto: VM['setModalPhoto']
  closeModal: VM['closeModal']
  handleDeletePhoto: VM['handleDeletePhoto']
}

/**
 * Fotograf lightbox'i.
 *
 * ORTAK `Modal` TABANINA TASINMADI (bilerek): bu bir lightbox -- siyah, %75 opak
 * bir zemin uzerinde kenarliksiz gorsel gosterir. `Modal`'in beyaz paneli, p-8
 * padding'i ve baslik satiri gorseli kucultup cerceveler; `panelClassName` ile
 * bunlarin hepsini geri almak tabani "kabuk" olmaktan cikarirdi.
 * Tasimanin asil kazanci hata slotu; bu bilesende gosterilecek bir hata YOK:
 * silme akisi `DeleteConfirmModal`e devrediliyor ve hata orada gosteriliyor.
 */
const PhotoModal: React.FC<PhotoModalProps> = ({
  canDeletePhotos,
  modalPhoto,
  setModalPhoto,
  closeModal,
  handleDeletePhoto,
}) => {
  return (
    <>
      {/* Photo Modal */}
      {modalPhoto && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-[999999] flex items-center justify-center"
          onClick={closeModal}
          onKeyDown={(e) => e.key === 'Escape' && closeModal()}
          tabIndex={0}
        >
          <div 
            className="max-w-4xl max-h-full mx-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all duration-200"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="bg-white rounded-lg overflow-hidden">
              <div className="relative">
                {(() => {
                  const currentPhoto = modalPhoto.photos[modalPhoto.currentIndex]
                  const isPcdFile = currentPhoto && (
                    ('key' in currentPhoto && currentPhoto.key?.toLowerCase().endsWith('.pcd')) ||
                    ('name' in currentPhoto && currentPhoto.name?.toLowerCase().endsWith('.pcd'))
                  )
                  
                  if (isPcdFile) {
                    return (
                      <div className="w-full h-96 flex items-center justify-center bg-gray-200">
                        <div className="text-center">
                          <div className="w-20 h-20 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                          </div>
                          <p className="text-lg font-medium text-gray-700 mb-2">PCD Dosyası - 360° Görüntü</p>
                          <p className="text-sm text-gray-500">Bu dosya 360 derece görüntüleyici gerektirir</p>
                          <p className="text-xs text-gray-400 mt-2">Gelecek güncellemede özel viewer eklenecek</p>
                        </div>
                      </div>
                    )
                  }
                  
                  return (
                    <img
                      src={currentPhoto && 'url' in currentPhoto 
                        ? currentPhoto.url 
                        : currentPhoto ? URL.createObjectURL(currentPhoto as File) : ''}
                      alt={`Photo ${modalPhoto.currentIndex + 1}`}
                      className="w-full max-h-96 object-contain"
                    />
                  )
                })()}
              </div>
              
              <div className="p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm text-gray-600">
                    {modalPhoto.currentIndex + 1} / {modalPhoto.photos.length} - {getRoomDisplayName(modalPhoto.roomNumber)}
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setModalPhoto({
                        ...modalPhoto,
                        currentIndex: modalPhoto.currentIndex > 0 ? modalPhoto.currentIndex - 1 : modalPhoto.photos.length - 1
                      })}
                      className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm"
                      disabled={modalPhoto.photos.length <= 1}
                    >
                      Önceki
                    </button>
                    <button
                      onClick={() => setModalPhoto({
                        ...modalPhoto,
                        currentIndex: modalPhoto.currentIndex < modalPhoto.photos.length - 1 ? modalPhoto.currentIndex + 1 : 0
                      })}
                      className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm"
                      disabled={modalPhoto.photos.length <= 1}
                    >
                      Sonraki
                    </button>
                  </div>
                </div>
                
                {/* Photo Name and Delete Button */}
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-700">
                    {(() => {
                      const currentPhoto = modalPhoto.photos[modalPhoto.currentIndex]
                      if (currentPhoto && 'originalName' in currentPhoto) {
                        return currentPhoto.originalName || currentPhoto.key || 'İsimsiz Fotoğraf'
                      } else if (currentPhoto && 'name' in currentPhoto) {
                        return (currentPhoto as File).name
                      }
                      return 'İsimsiz Fotoğraf'
                    })()}
                  </div>
                  
                  {(() => {
                    const currentPhoto = modalPhoto.photos[modalPhoto.currentIndex]
                    // Only show delete button for uploaded photos (with 'key' property) and admin users
                    if (currentPhoto && 'key' in currentPhoto && canDeletePhotos) {
                      return (
                        <button
                          onClick={handleDeletePhoto}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm transition-colors"
                        >
                          Sil
                        </button>
                      )
                    }
                    return null
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PhotoModal
